import type { VariantProps } from "tailwind-variants";

import { tv } from "../utils/tv";
import { colorVariants, radius, dataFocusVisibleClasses } from "../utils";

/**
 * Button wrapper **Tailwind Variants** component
 *
 * const classNames = button({...})
 *
 * @example
 * <button
 *  className={classNames())}
 *  data-pressed={true/false}
 *  data-hover={true/false}
 *  data-focus={true/false}
 *  data-focus-visible={true/false}
 * >
 *   Button
 * </button>
 */
const button = tv({
  base: [
    "z-0",
    "relative",
    "group",
    "subpixel-antialiased",
    "appearance-none",
    "outline-none",
    "box-border",
    "select-none",
    "inline-flex",
    "items-center",
    "justify-center",
  ],
  variants: {
    variant: {
      unstyled: "box-shadow-none",
      solid: "",
      outlined: "border-medium bg-transparent",
      link: "box-shadow-none",
      auxiliary: "",
      buttonGroup: colorVariants.buttonGroup.default,
    },
    size: {
      xs: "p-2 min-w-8 h-8 text-xs gap-1 [&>svg]:w-4 [&>svg]:h-4",
      sm: "p-3 min-w-10 h-10 text-sm gap-2 [&>svg]:w-4 [&>svg]:h-4",
      md: "p-3 min-w-12 h-12 text-base gap-2 [&>svg]:w-6 [&>svg]:h-6",
      lg: "p-4 min-w-14 h-14 text-lg gap-2 [&>svg]:w-6 [&>svg]:h-6",
    },
    color: {
      primary: "",
      secondary: "",
      tertiary: "",
      success: "",
      warning: "",
      danger: "",
    },
    radius,
    fullWidth: {
      true: "w-full",
    },
    isDisabled: {
      true: ["pointer-events-none", "![box-shadow:0px_1px_2px_0px_rgba(31,31,31,0.15)]"],
    },
    isLoading: {
      true: "pointer-events-none",
    },
    isIconOnly: {
      true: "px-0 !gap-0",
      false: "[&>svg]:max-w-[theme(spacing.8)]",
    },
    disableAnimation: {
      true: "!transition-none",
      false: "transition-transform-colors-opacity motion-reduce:transition-none",
    },
    disableShadows: {
      true: {
        base: "!shadow-none !box-shadow-none",
      },
      false: "",
    },
  },
  defaultVariants: {
    size: "lg",
    variant: "solid",
    color: "primary",
    radius: "sm",
    fullWidth: false,
    isDisabled: false,
    isLoading: false,
    disableShadows: false,
  },
  compoundVariants: [
    {
      variant: ["outlined", "solid", "link", "auxiliary"],
      class: [
        "whitespace-nowrap",
        "min-w-max",
        "font-bold",
        "overflow-hidden",
        "tap-highlight-transparent",
        "data-[pressed=true]:scale-[0.97]",
        // focus ring
        ...dataFocusVisibleClasses,
      ],
    },
    // solid / color
    {
      variant: "solid",
      color: "primary",
      class: colorVariants.solid.primary,
    },
    {
      variant: "solid",
      color: "secondary",
      class: colorVariants.solid.secondary,
    },
    {
      variant: "solid",
      color: "tertiary",
      class: [...colorVariants.solid.tertiary],
    },
    {
      variant: "solid",
      color: "success",
      class: colorVariants.solid.success,
    },
    {
      variant: "solid",
      color: "warning",
      class: colorVariants.solid.warning,
    },
    {
      variant: "solid",
      color: "danger",
      class: colorVariants.solid.danger,
    },
    // outlined / color
    {
      variant: "outlined",
      color: "primary",
      class: colorVariants.outlined.primary,
    },
    {
      variant: "outlined",
      color: "secondary",
      class: colorVariants.outlined.secondary,
    },
    {
      variant: "outlined",
      color: "tertiary",
      class: colorVariants.outlined.tertiary,
    },
    {
      variant: "outlined",
      color: "success",
      class: colorVariants.outlined.success,
    },
    {
      variant: "outlined",
      color: "warning",
      class: colorVariants.outlined.warning,
    },
    {
      variant: "outlined",
      color: "danger",
      class: colorVariants.outlined.danger,
    },
    // link / color
    {
      variant: "link",
      color: "primary",
      class: colorVariants.link.primary,
    },
    {
      variant: "link",
      color: "secondary",
      class: colorVariants.link.secondary,
    },
    {
      variant: "link",
      color: "tertiary",
      class: colorVariants.link.tertiary,
    },
    {
      variant: "link",
      color: "success",
      class: colorVariants.link.success,
    },
    {
      variant: "link",
      color: "warning",
      class: colorVariants.link.warning,
    },
    {
      variant: "link",
      color: "danger",
      class: colorVariants.link.danger,
    },
    // auxiliary
    {
      variant: "auxiliary",
      class: colorVariants.auxiliary.default,
    },
    // unstyled
    {
      variant: "unstyled",
      class: ["border-none outline-none", "!transition-none", "p-0 m-0"],
    },
    // Loading states
    {
      variant: "solid",
      color: "primary",
      isLoading: true,
      class: "bg-fx-primary-400 text-fx-neutral-50",
    },
    {
      variant: "solid",
      color: "secondary",
      isLoading: true,
      class: "bg-fx-secondary-400 text-fx-neutral-50",
    },
    {
      variant: "solid",
      color: "tertiary",
      isLoading: true,
      class: "bg-fx-tertiary-400 text-fx-neutral-50",
    },
    {
      variant: "solid",
      color: "success",
      isLoading: true,
      class: "bg-fx-success-400 text-fx-neutral-50",
    },
    {
      variant: "solid",
      color: "warning",
      isLoading: true,
      class: "bg-fx-warningY-400 text-fx-neutral-900",
    },
    {
      variant: "solid",
      color: "danger",
      isLoading: true,
      class: "bg-fx-danger-400 text-fx-neutral-50",
    },
    {
      variant: "outlined",
      color: "primary",
      isLoading: true,
      class: "border-fx-primary-400 text-fx-primary-400",
    },
    {
      variant: "outlined",
      color: "secondary",
      isLoading: true,
      class: "border-fx-secondary-400 text-fx-secondary-400",
    },
    {
      variant: "outlined",
      color: "tertiary",
      isLoading: true,
      class: "border-fx-tertiary-400 text-fx-tertiary-400",
    },
    {
      variant: "outlined",
      color: "success",
      isLoading: true,
      class: "border-fx-success-400 text-fx-success-400",
    },
    {
      variant: "outlined",
      color: "warning",
      isLoading: true,
      class: "border-fx-warningY-400 text-fx-warningY-400",
    },
    {
      variant: "outlined",
      color: "danger",
      isLoading: true,
      class: "border-fx-danger-400 text-fx-danger-400",
    },
    {
      variant: "link",
      color: "primary",
      isLoading: true,
      class: "text-fx-primary-400",
    },
    {
      variant: "link",
      color: "secondary",
      isLoading: true,
      class: "text-fx-secondary-400",
    },
    {
      variant: "link",
      color: "tertiary",
      isLoading: true,
      class: "text-fx-tertiary-400",
    },
    {
      variant: "link",
      color: "success",
      isLoading: true,
      class: "text-fx-success-400",
    },
    {
      variant: "link",
      color: "warning",
      isLoading: true,
      class: "text-fx-warningY-400",
    },
    {
      variant: "link",
      color: "danger",
      isLoading: true,
      class: "text-fx-danger-400",
    },
    {
      isIconOnly: true,
      size: "xs",
      class: "min-w-8 w-8 h-8",
    },
    {
      isIconOnly: true,
      size: "sm",
      class: "min-w-10 w-10 h-10",
    },
    {
      isIconOnly: true,
      size: "md",
      class: "min-w-12 w-12 h-12",
    },
    {
      isIconOnly: true,
      size: "lg",
      class: "min-w-14 w-14 h-14",
    },

    // variant button group / size
    {
      variant: "buttonGroup",
      size: "xs",
      class: ["px-4t"],
    },
    // disable shadows
    {
      disableShadows: true,
      class: ["!shadow-none !box-shadow-none"],
    },
    {
      disableShadows: true,
      variant: "auxiliary",
      class: ["!shadow-none !box-shadow-none", "border border-neutral-200"],
    },
    {
      isDisabled: true,
      disableShadows: true,
      class: ["!shadow-none", "!box-shadow-none", "![box-shadow:none]"],
    },
  ],
});

/**
 * ButtonGroup wrapper **Tailwind Variants** component
 *
 * const classNames = buttonGroup({...})
 *
 * @example
 * <div role="group" className={classNames())}>
 *   // button elements
 * </div>
 */
const buttonGroup = tv({
  base: [
    "inline-flex items-center justify-center relative",
    "h-auto",
    "m-0 p-0",
    "border border-neutral-300/90",
    // shadow light
    "[box-shadow:0px_1px_2px_0px_rgba(31,31,31,0.1)]",
    // shadow dark
    "dark:[box-shadow:0px_1px_2px_0px_rgba(255,255,255,0.1)]",
  ],
  variants: {
    fullWidth: {
      true: "w-full",
    },
    radius: {
      ...radius,
      sm: "rounded-[9px]",
    },
  },
  defaultVariants: {
    fullWidth: false,
    radius: "sm",
  },
});

export type ButtonVariantProps = VariantProps<typeof button>;
export type ButtonGroupVariantProps = VariantProps<typeof buttonGroup>;

export { button, buttonGroup };
